import type {
  ClientChat,
  ClientPerson,
  ClientPersonReference
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { BlockedChatState } from '@/features/chats/components/BlockedChatState';
import { ChatLayout } from '@/features/chats/components/ChatLayout';
import { ChatMessageEditor } from '@/features/chats/components/ChatMessageEditor';
import { ChatMessages } from '@/features/chats/components/ChatMessages';
import { ChatNavigationBar } from '@/features/chats/components/ChatNavigationBar';
import { EmptyChatState } from '@/features/chats/components/EmptyChatState';
import { ExistingChat } from '@/features/chats/components/ExistingChat';
import { useChatPartner } from '@/features/chats/hooks/useChatPartner';

type Props = {
  sendMessage: (
    message: string,
    temporaryMediaItemIds: string[]
  ) => Promise<string>;
  existingChat?: ClientChat;
  entityPerson?: ClientPerson | ClientPersonReference;
  /**
   * Indicates that related data, such as existing chat and entity information, is still loading.
   *
   * @see {@link Props.entityPerson} and {@link Props.existingChat}
   */
  loading: boolean;
};

export const NewChatOnEntity: FC<Props> = ({
  sendMessage,
  existingChat,
  entityPerson,
  loading
}) => {
  const [startedChatId, setStartedChatId] = useState<string>();

  const handleSendStartMessage = useCallback(
    async (message: string, temporaryMediaItemIds: string[]) => {
      const newChatId = await sendMessage(message, temporaryMediaItemIds);
      setStartedChatId(newChatId);
    },
    [sendMessage]
  );

  const existingChatPartner = useChatPartner(existingChat);

  const messages = useMemo(() => {
    const existingChatId = startedChatId ?? existingChat?.id;
    if (existingChatId) {
      return <ChatMessages chatId={existingChatId} />;
    }
    return loading ? (
      <View style={{ marginTop: 80 }}>
        <ActivityIndicator size={80} />
      </View>
    ) : (
      <EmptyChatState />
    );
  }, [existingChat?.id, loading, startedChatId]);

  if (startedChatId) {
    // The initial message has been sent. From here on, it continues as a normal chat
    return <ExistingChat chatId={startedChatId} autofocus />;
  }

  const partner = existingChatPartner ?? entityPerson;
  const deleted = partner?.deleted ?? false;
  const blocked = partner?.filterStatus === 'BLOCKED';

  return (
    <ChatLayout
      header={
        <ChatNavigationBar
          chatPartner={partner}
          chatId={startedChatId}
          archived={existingChat?.archived}
        />
      }
      messages={blocked ? <BlockedChatState /> : messages}
      editor={
        blocked || deleted ? undefined : (
          <ChatMessageEditor
            onSendChatMessage={handleSendStartMessage}
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        )
      }
    />
  );
};
